import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import { utc } from './dhfUi';
import { Diff } from './variantsUi';
import '../../styles/DocReview.css';
import '../../styles/Dhf.css';
import '../../styles/DhfVariants.css';

const KIND = {
  conflict: { tone: 'warn', text: 'CONFLICT — both changed' },
  origin_changed: { tone: 'blue', text: 'Changed in the parent' },
  new_in_origin: { tone: 'green', text: 'New in the parent' },
  removed_in_origin: { tone: 'grey', text: 'Removed from the parent' },
  moved: { tone: 'blue', text: 'Moved in the parent' },
};

const CHOICES = {
  origin_changed: [['take', 'Take the parent’s'], ['keep', 'Keep the branch’s'], ['edit', 'Edit']],
  conflict: [['take', 'Take the parent’s'], ['keep', 'Keep the branch’s'], ['edit', 'Edit']],
  new_in_origin: [['add', 'Add to the branch'], ['skip', 'Leave it out of the branch']],
  removed_in_origin: [['remove', 'Remove from the branch'], ['keep', 'Keep in the branch']],
  moved: [['move', 'Move it in the branch too'], ['keep', 'Keep it where it is']],
};

const toHtml = (t) => t.split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>`).join('');
const plain = (h) => (h || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

function Side({ cap, side, segs }) {
  return (
    <div className="dhfv-three-cell">
      <div className="dhfv-cap">{cap}</div>
      {!side ? <span className="dhfv-absent">—</span> : segs ? (
        <><div className="dhfv-cmp-name"><Diff segs={segs.name} /></div><div><Diff segs={segs.description} /></div></>
      ) : (
        <><div className="dhfv-cmp-name">{side.name}</div><div className="dhfv-cmp-desc">{plain(side.description)}</div></>
      )}
    </div>
  );
}

/**
 * Pull from the parent (mockup screen 4): BASE = the parent when this branch was forked or last
 * pulled, NOW = the parent today, VARIANT = this one. Where only the parent changed, its text is
 * taken by default; a change on both sides is a conflict with no default. Applying needs a choice
 * for every row — the base then moves to today, so an undecided change would be lost.
 */
export default function DhfVariantPullPage() {
  const { setupId, vid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dec, setDec] = useState({});
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, v] = await Promise.all([dhfApi.pullPlan(setupId, vid), dhfApi.variant(setupId, vid)]);
      setData(p);
      setDoc(v);
      const d = {};
      p.plan.forEach((r) => {
        if (r.default) d[r.id] = { action: r.default };
        else if (r.kind === 'conflict') d[r.id] = { action: null, name: r.mine?.name };
      });
      setDec(d);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [setupId, vid]);
  useEffect(() => { load(); }, [load]);

  const set = (id, patch) => setDec((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  const decided = data ? data.plan.filter((r) => dec[r.id]?.action).length : 0;

  const apply = async () => {
    setBusy(true);
    try {
      const body = {};
      data.plan.forEach((r) => {
        const d = dec[r.id];
        // Text edited here is plain; untouched, the branch's own HTML is kept.
        const desc = d.text != null ? toHtml(d.text) : r.mine?.description;
        body[r.id] = d.action === 'edit'
          ? { action: 'edit', name: d.name ?? r.mine?.name, description: desc }
          : { action: d.action };
      });
      await dhfApi.pull(setupId, vid, body);
      nav(`/dhf/${setupId}/variant/${vid}`);
    } catch (e) {
      setError(e.detail?.missing ? `Decide every change first (${e.detail.missing.length} left).` : e.message);
      setBusy(false);
    }
  };

  if (error && !data) return <div className="drv-page"><div className="drv-error" role="alert">{error}</div></div>;
  if (!data) return <div className="drv-page"><div className="drv-muted">Loading…</div></div>;
  const n = data.plan.length;

  return (
    <div className="drv-page">
      <div className="dhf-doc-bar">
        <div className="dhf-doc-bar-l">
          <Link to={`/dhf/${setupId}/variant/${vid}`}>← {data.variant.name}</Link>
          <strong>Pull from {data.parent_label}</strong>
          <span className="drv-muted">{n} change{n === 1 ? '' : 's'} since this branch's base ({utc(data.base_at)})</span>
        </div>
        <div className="dhf-doc-bar-r">
          <span className="drv-muted">{decided} of {n} decided</span>
          <Link className="drv-btn" to={`/dhf/${setupId}/variant/${vid}`}>Cancel</Link>
          <button type="button" className="drv-btn drv-btn--primary" disabled={busy || !n || decided < n} onClick={apply}>
            {busy ? 'Applying…' : 'Apply to branch'}
          </button>
        </div>
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
      <div className="dhf-note" style={{ marginTop: 0 }}>
        <strong>Base</strong> = {data.parent_label} when this branch was forked or last pulled. <strong>Now</strong> = {data.parent_label} today
        {doc && !doc.parent_id ? ' (as the last DHF build read it — Refresh the DHF for the very latest)' : ''}. Applying changes this branch only, and moves its base to now.
      </div>

      {n === 0 && <div className="drv-card dhfv-empty">Nothing to pull — this branch is up to date with {data.parent_label}.</div>}

      <div className="dhfv-pull-list">
        {data.plan.map((r) => {
          const k = KIND[r.kind];
          const d = dec[r.id] || {};
          const head = r.now || r.base || r.mine;
          return (
            <div key={r.id} className={`drv-card dhfv-pull dhfv-pull--${r.kind}${d.action ? '' : ' dhfv-pull--open'}`}>
              <div className="dhfv-pull-h">
                <span className={`dhfv-state dhfv-state--${k.tone}`}>{k.text}</span>
                {head.key && <span className="dhf-key">{head.key}</span>}
                <strong>{head.name}</strong>
                <span className="drv-muted">{r.kind === 'moved' ? `${r.from_para || 'top level'} → ${r.para || 'top level'}` : r.para}</span>
              </div>
              {(r.kind === 'origin_changed' || r.kind === 'conflict') && (
                <div className="dhfv-three">
                  <Side cap="Base" side={r.base} />
                  <Side cap="Now" side={r.now} segs={r.diff_now} />
                  <Side cap="Branch" side={r.mine} segs={r.kind === 'conflict' ? r.diff_mine : null} />
                </div>
              )}
              {r.kind === 'removed_in_origin' && r.mine_changed && <p className="dhfv-tiny">This branch changed it, so it is kept by default (as a branch-only item, not traced).</p>}
              <div className="dhfv-choice" role="radiogroup" aria-label={`Resolve ${head.name}`}>
                {CHOICES[r.kind].map(([v, l]) => (
                  <label key={v}><input type="radio" name={`c-${r.id}`} checked={d.action === v} onChange={() => set(r.id, { action: v })} /> {l}</label>
                ))}
              </div>
              {d.action === 'edit' && (
                <div className="dhfv-edit">
                  <input aria-label="Merged name" className="dhfv-edit-name" value={d.name ?? r.mine?.name ?? ''}
                         onChange={(e) => set(r.id, { name: e.target.value })} />
                  <textarea aria-label="Merged description" rows={3} value={d.text ?? plain(r.mine?.description)}
                            onChange={(e) => set(r.id, { text: e.target.value })} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
